import { pendingRequestService } from '@/services/api/adminApi';
import { getErrorMessage } from '@/services/api/helpers';
import { requestAlertSound } from '@/services/requestAlertSound';

const POLL_INTERVAL_MS = 30000;
const read = (value, key) => value?.[key] ?? value?.[key.charAt(0).toUpperCase() + key.slice(1)];
const count = (value) => Math.max(0, Number(value) || 0);

let timer = null;
let inFlight = null;
let generation = 0;
let started = false;
let focusHandler = null;
let visibilityHandler = null;

function markerAdvanced(previous, next) {
  if (next === null || next === undefined || next === '') return false;
  if (previous === null || previous === undefined || previous === '') return true;
  const previousNumber = Number(previous);
  const nextNumber = Number(next);
  if (Number.isFinite(previousNumber) && Number.isFinite(nextNumber)) return nextNumber > previousNumber;
  return String(next) !== String(previous);
}

const initialState = () => ({
  pendingLeaveRequests: 0,
  pendingLetterRequests: 0,
  latestPendingLeaveApprovalId: null,
  latestPendingLetterRequestId: null,
  loading: false,
  loaded: false,
  error: '',
});

export default {
  namespaced: true,
  state: initialState,
  getters: {
    total: (state) => state.pendingLeaveRequests + state.pendingLetterRequests,
  },
  mutations: {
    reset(state) { Object.assign(state, initialState()); },
    loading(state) { state.loading = true; state.error = ''; },
    loaded(state, summary) {
      state.pendingLeaveRequests = count(read(summary, 'pendingLeaveRequests'));
      state.pendingLetterRequests = count(read(summary, 'pendingLetterRequests'));
      state.latestPendingLeaveApprovalId = read(summary, 'latestPendingLeaveApprovalId') ?? null;
      state.latestPendingLetterRequestId = read(summary, 'latestPendingLetterRequestId') ?? null;
      state.loading = false;
      state.loaded = true;
      state.error = '';
    },
    failed(state, error) { state.loading = false; state.error = error; },
  },
  actions: {
    refresh({ state, commit }) {
      if (inFlight) return inFlight;
      const requestGeneration = generation;
      const previous = {
        loaded: state.loaded,
        leave: state.latestPendingLeaveApprovalId,
        letter: state.latestPendingLetterRequestId,
      };
      commit('loading');
      inFlight = pendingRequestService.getSummary()
        .then((summary) => {
          if (requestGeneration !== generation) return;
          const nextLeave = read(summary, 'latestPendingLeaveApprovalId') ?? null;
          const nextLetter = read(summary, 'latestPendingLetterRequestId') ?? null;
          const nextTotal = count(read(summary, 'pendingLeaveRequests')) + count(read(summary, 'pendingLetterRequests'));
          commit('loaded', summary);
          const isInitialAlert = !previous.loaded && nextTotal > 0;
          const hasNewRequest = previous.loaded && (
            markerAdvanced(previous.leave, nextLeave) || markerAdvanced(previous.letter, nextLetter)
          );
          if (isInitialAlert || hasNewRequest) requestAlertSound.notify();
        })
        .catch((error) => {
          if (requestGeneration === generation) {
            commit('failed', getErrorMessage(error, 'Request alerts could not be refreshed.'));
          }
        })
        .finally(() => {
          if (requestGeneration === generation) inFlight = null;
        });
      return inFlight;
    },
    start({ dispatch, commit }) {
      if (started) return dispatch('refresh');
      started = true;
      generation += 1;
      inFlight = null;
      commit('reset');
      const refreshVisible = () => {
        if (typeof document === 'undefined' || document.visibilityState === 'visible') dispatch('refresh');
      };
      focusHandler = refreshVisible;
      visibilityHandler = refreshVisible;
      if (typeof window !== 'undefined') window.addEventListener('focus', focusHandler);
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', visibilityHandler);
      timer = setInterval(refreshVisible, POLL_INTERVAL_MS);
      return dispatch('refresh');
    },
    stop({ commit }) {
      started = false;
      generation += 1;
      inFlight = null;
      if (timer) clearInterval(timer);
      timer = null;
      if (typeof window !== 'undefined' && focusHandler) window.removeEventListener('focus', focusHandler);
      if (typeof document !== 'undefined' && visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      focusHandler = null;
      visibilityHandler = null;
      requestAlertSound.reset();
      commit('reset');
    },
  },
};
